import { useMemo } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { useOrgBatchManagement, useOrgTrainerPool } from '@/lib/hooks/useOrg';
import type { OrgTrainer } from '@/lib/api/org';

export function ManageTrainersDialog({
  batchId, open, onOpenChange, current,
}: {
  batchId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  current: OrgTrainer[];
}) {
  const { data: pool } = useOrgTrainerPool(open);
  const m = useOrgBatchManagement(batchId);
  const currentIds = new Set(current.map((t) => t.user_id));
  const available = useMemo(() => (pool ?? []).filter((t) => !currentIds.has(t.user_id)), [pool, currentIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Manage trainers</DialogTitle></DialogHeader>

        <div className="mt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#9ca3af]">Assigned</p>
          {current.length === 0 ? (
            <p className="py-3 text-sm text-[#9ca3af]">No trainers assigned yet.</p>
          ) : (
            <div className="mt-2 space-y-1">
              {current.map((t) => {
                const pending = m.removeTrainer.isPending && m.removeTrainer.variables?.userId === t.user_id;
                return (
                  <div key={t.user_id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[#fafafa]">
                    <OrgAvatar name={t.name} size={30} />
                    <span className="flex-1 truncate text-sm font-medium text-[#111827]">{t.name}</span>
                    <button
                      type="button"
                      disabled={pending}
                      onClick={() => m.removeTrainer.mutate({ batchId, userId: t.user_id })}
                      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 border-t border-[#ececec] pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#9ca3af]">Available trainers</p>
          {available.length === 0 ? (
            <p className="py-3 text-sm text-[#9ca3af]">No other trainers in this organization.</p>
          ) : (
            <div className="mt-2 max-h-56 space-y-1 overflow-y-auto">
              {available.map((t) => {
                const pending = m.assignTrainer.isPending && m.assignTrainer.variables?.userId === t.user_id;
                return (
                  <div key={t.user_id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[#fafafa]">
                    <OrgAvatar name={t.name} size={30} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-[#111827]">{t.name}</p>
                      <p className="truncate text-xs text-[#9ca3af]">{t.email}</p>
                    </div>
                    <button
                      type="button"
                      disabled={pending}
                      onClick={() => m.assignTrainer.mutate({ batchId, userId: t.user_id })}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[#ececec] px-3 py-1.5 text-sm font-medium text-[#ff5a1f] hover:bg-[#fff1ec] disabled:opacity-50"
                    >
                      {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Assign
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
